<template>
  <v-card class="client feedback-requests form">
    <v-card-title class="pa-1">
      <v-spacer />
      <v-btn icon @click.stop="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-1" style="max-height: 30vh; overflow-y: scroll">
      <div class="text-xs-right">
        <v-btn
          @click="sortBy = 'firstNames'"
          :color="sortBy === 'firstNames' ? '' : themes.client.light.muted"
          flat
          small
        >
          <v-icon small>mdi-sort-descending</v-icon>By Name
        </v-btn>
        <v-btn
          @click="sortBy = 'title'"
          :color="sortBy === 'title' ? '' : themes.client.light.muted"
          flat
          small
        >
          <v-icon small>mdi-sort-descending</v-icon>By Title
        </v-btn>
      </div>
      <div class="px-2">
        <v-text-field
          v-if="colleagueCount > 9"
          v-model="search"
          placeholder="Find Colleague"
          hide-details
          solo
        />
      </div>
      <v-list>
        <v-list-tile
          v-for="colleague in colleagues"
          :key="colleague.id"
          @click="toggleInResponders(colleague)"
          avatar
        >
          <v-badge
            :color="themes.client.light.success"
            class="selected-badge"
            left
            bottom
            overlap
            :value="responders.indexOf(colleague.id) >= 0"
          >
            <v-icon slot="badge" dark small>mdi-check-circle</v-icon>
            <v-list-tile-avatar
              :class="{
                'unselected-avatar': responders.indexOf(colleague.id) < 0,
              }"
            >
              <img
                :src="colleague.avatar || '/img/missing/avatars/original.png'"
                :class="[
                  themes.client.light.success,
                  { 'pa-1': responders.indexOf(colleague.id) >= 0 },
                ]"
                v-img-orientation-changer
              />
            </v-list-tile-avatar>
          </v-badge>

          <v-list-tile-content>
            <v-list-tile-title
              :class="{
                'font-weight-bold': responders.indexOf(colleague.id) >= 0,
              }"
              v-text="`${colleague.firstNames} ${colleague.lastName}`"
            />
            <v-list-tile-sub-title>
              <span v-if="colleague.title">{{ colleague.title }}</span>
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>

        <!-- TODO -->
        <!-- Slot: no-data -->
        <!-- Slot: no-results -->
      </v-list>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="!responders.length"
        :color="themes.client.light.muted"
        @click="responders = []"
        flat
      >
        Clear
      </v-btn>
      <v-menu v-model="confirm" max-width="200" top left>
        <v-btn
          slot="activator"
          :disabled="!responders.length"
          :color="themes.client.light.successButton"
          :loading="loading"
          @click.stop="confirm = true"
        >
          Share Now
        </v-btn>
        <v-card v-if="confirm && responders.length > 0">
          <v-card-text>
            You are about to <strong>Share</strong> this profile with
            {{ responderColleagues[0].firstNames }}
            <span v-if="responders.length > 1">
              and {{ responders.length - 1 }}
              {{ (responders.length - 1) | pluralize("other") }}.
            </span>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              flat
              :color="themes.client.light.muted"
              @click="confirm = false"
            >
              Cancel
            </v-btn>
            <v-btn
              :color="themes.client.light.successButton"
              @click="submit"
              :loading="loading"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import _ from "lodash";

export default {
  created() {
    let promises = [this.$store.dispatch("client/users/fetchAll")];
    if (!this.allFeedbackRequests.length && !this.loadingFeedbackRequests) {
      promises.push(
        this.$store.dispatch("client/feedbackRequests/fetchAll", {
          opportunity: this.$route.params.id,
        })
      );
    }

    Promise.all(promises).finally(() => (this.loading = false));
  },

  props: {
    applicationsIds: { type: Array, required: true },
  },

  data() {
    return {
      loading: true,
      confirm: false,
      search: "",
      sortBy: "firstNames",
      responders: [],
    };
  },

  computed: {
    ...mapGetters({
      users: "client/users/all",
      user: "auth/user",
    }),

    ...mapState("client/feedbackRequests", {
      allFeedbackRequests: "all",
      loadingFeedbackRequests: "loading",
    }),

    feedbackRequests() {
      if (this.applicationsIds.length > 1) return [];
      return this.$store.getters["client/feedbackRequests/forApplication"](
        this.applicationsIds[0]
      );
    },

    colleagues() {
      if (this.users.length === 0) return [];

      let users = JSON.parse(JSON.stringify(this.users));
      return users
        .filter((u) => !u.primary && u.opportunities.length === 0)
        .filter((u) => u.id !== this.user.id)
        .filter((u) =>
          RegExp(this.search, "gi").test(`${u.firstNames}${u.lastName}`)
        )
        .sort((a, b) => {
          if (a[this.sortBy] > b[this.sortBy]) return 1;
          if (a[this.sortBy] < b[this.sortBy]) return -1;
          return 0;
        });
    },

    colleagueCount() {
      let users = JSON.parse(JSON.stringify(this.users));
      return users
        .filter((u) => !u.primary && u.opportunities.length === 0)
        .filter((u) => u.id !== this.user.id).length;
    },

    responderColleagues() {
      return this.colleagues.filter((c) => this.responders.indexOf(c.id) >= 0);
    },
  },

  methods: {
    toggleInResponders(responder) {
      const index = this.responders.indexOf(responder.id);

      if (index >= 0) this.responders.splice(index, 1);
      if (index < 0) this.responders.push(responder.id);
    },

    recentFeedbackRequestFor(responder) {
      if (this.feedbackRequests.length < 1) return [];

      let feedbackRequests = JSON.parse(JSON.stringify(this.feedbackRequests));

      feedbackRequests.sort(({ createdAt: a }, { createdAt: b }) => {
        if (new Date(a) < new Date(b)) return 1;
        if (new Date(a) > new Date(b)) return -1;
        return 0;
      });
      return feedbackRequests.find(
        (request) => request.responder.id === responder.id
      );
    },

    submit() {
      if (
        confirm &&
        this.applicationsIds.length > 0 &&
        this.responders.length > 0
      ) {
        this.$store
          .dispatch("client/feedbackRequests/create", {
            opportunityApplications: this.applicationsIds,
            responders: this.responders,
          })
          .then(
            (data) => {
              this.responders = [];
              this.$emit("close");
              this.$store.dispatch("snackbarSuccess", "Shared Successfully");
              this.applicationsIds.forEach((id) => {
                this.$store.dispatch("client/applications/fetch", { id });
              });
            },
            (errors) => {
              this.$store.dispatch("snackbarError", "An error occured");
            }
          );
      }
    },
  },
};
</script>

<style lang="scss">
.client.feedback-requests.form {
  .selected-badge span {
    bottom: -2px;
    left: -2px;
    height: 16px;
    width: 16px;
  }

  .unselected-avatar img {
    filter: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg'><filter id='grayscale'><feColorMatrix type='matrix' values='0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0 0 0 1 0'/></filter></svg>#grayscale"); /* Firefox 10+, Firefox on Android */
    filter: grayscale(100%);
    -moz-filter: grayscale(100%);
    -ms-filter: grayscale(100%);
    -o-filter: grayscale(100%);
    filter: gray; /* IE6-9 */
    -webkit-filter: grayscale(100%); /* Chrome 19+, Safari 6+, Safari 6+ iOS */
  }
}
</style>
